const NON_TRANSLATABLE_TAGS = [
  'script',
  'style',
  'code',
  'svg',
  'video',
  'audio',
  'source',
  'canvas',
  'object',
  'iframe',
  'math',
  'pre',
  'template',
];

const TRANSLATABLE_ATTRS = ['alt', 'title'];

/**
 * Normalizes HTML entities and excessive whitespace
 * before parsing / reconstruction.
 *
 * @param {string} html
 * @returns {string}
 */
function normalizeEntities(html) {
  return html.replace(/&nbsp;|&#160;|nbsp;/gi, ' ').replace(/\s{2,}/g, ' ');
}

/**
 * Checks whether an attribute value should be translated.
 *
 * @param {string|null} value
 * @returns {boolean}
 */
function shouldTranslateAttr(value) {
  if (!value || !value.trim()) {
    return false;
  }

  const trimmed = value.trim();

  if (trimmed === '????') {
    return false;
  }

  return !(trimmed.includes('__HREF_') ||
    trimmed.includes('__SRC_') ||
    trimmed.includes('__NONTR_') ||
    trimmed.includes('__TR_'));
}

/**
 * Extracts sequential translatable chunks from HTML while preserving:
 * - Original structural skeleton
 * - Non-translatable elements
 * - Protected attributes (href/src)
 * - Translatable attributes (alt/title)
 *
 * The output can later be reconstructed using mergeChunks().
 *
 * @param {string} html
 * @param {number} maxChars - Maximum chunk size
 * @returns {{
 *   skeleton: string,
 *   translatable: Array<{
 *     key: string,
 *     html: string,
 *     type?: 'html' | 'attribute',
 *     attr?: string
 *   }>,
 *   nonTranslatable: Array,
 *   protectedAttrs: Array
 * }}
 */
export function extractSequentialChunks(html, maxChars = 25000) {
  html = normalizeEntities(html);

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const nonTranslatable = [];
  const protectedAttrs = [];
  const translatableAttrs = [];

  let nonTrIndex = 0;
  let hrefIndex = 0;
  let srcIndex = 0;
  let attrTrIndex = 0;

  /**
   * Replace non-translatable nodes with comment placeholders
   * and store their original HTML for later restoration.
   */
  doc.querySelectorAll(NON_TRANSLATABLE_TAGS.join(',')).forEach((el) => {
    const key = `__NONTR_${nonTrIndex++}__`;

    nonTranslatable.push({
      key,
      html: el.outerHTML,
    });

    el.replaceWith(doc.createComment(key));
  });

  /**
   * Protect href attributes from translation.
   */
  doc.querySelectorAll('a[href]').forEach((el) => {
    const key = `__HREF_${hrefIndex++}__`;

    protectedAttrs.push({
      attr: 'href',
      key,
      value: el.getAttribute('href'),
    });

    el.setAttribute('href', key);
  });

  /**
   * Protect src attributes from translation.
   */
  doc.querySelectorAll('[src]').forEach((el) => {
    const key = `__SRC_${srcIndex++}__`;

    protectedAttrs.push({
      attr: 'src',
      key,
      value: el.getAttribute('src'),
    });

    el.setAttribute('src', key);
  });

  /**
   * Extract translatable attributes such as alt/title.
   *
   * Example:
   * <img alt="Interaktívny SCRUM workshop">
   *
   * becomes:
   * <img alt="__TR_ATTR_0__">
   *
   * and "__TR_ATTR_0__" is sent as a separate translation item.
   */
  doc.querySelectorAll('*').forEach((el) => {
    TRANSLATABLE_ATTRS.forEach((attr) => {
      const value = el.getAttribute(attr);

      if (!shouldTranslateAttr(value)) {
        return;
      }

      const key = `__TR_ATTR_${attrTrIndex++}__`;

      translatableAttrs.push({
        key,
        html: value,
        type: 'attribute',
        attr,
      });

      el.setAttribute(attr, key);
    });
  });

  const processedHtml = doc.body.innerHTML;

  /**
   * Default case:
   * Single translatable chunk if within provider limit.
   */
  if (processedHtml.length <= maxChars) {
    return {
      skeleton: '__TR_0__',
      translatable: [
        {
          key: '__TR_0__',
          html: processedHtml,
          type: 'html',
        },
        ...translatableAttrs,
      ],
      nonTranslatable,
      protectedAttrs,
    };
  }

  /**
   * Fallback sequential splitting for oversized content.
   * Splits by raw character length while preserving order.
   */
  const translatable = [];
  const resultParts = [];

  for (let i = 0; i < processedHtml.length; i += maxChars) {
    const key = `__TR_${translatable.length}__`;

    translatable.push({
      key,
      html: processedHtml.slice(i, i + maxChars),
      type: 'html',
    });

    resultParts.push(key);
  }

  return {
    skeleton: resultParts.join('\n'),
    translatable: [...translatable, ...translatableAttrs],
    nonTranslatable,
    protectedAttrs,
  };
}

/**
 * Reconstructs original HTML by merging translated chunks
 * back into the preserved skeleton structure.
 *
 * Restores:
 * - Non-translatable elements
 * - Protected href/src attributes
 *
 * @param {string} skeleton
 * @param {Array} translatedChunks
 * @param {Array} nonTranslatable
 * @param {Array} protectedAttrs
 * @returns {string}
 */
export function mergeChunks(
  skeleton,
  translatedChunks,
  nonTranslatable,
  protectedAttrs = [],
) {
  let html = skeleton;

  /**
   * Replace translation placeholders with translated HTML chunks
   * and translated attribute values.
   */
  translatedChunks.forEach(({ key, html: translated }) => {
    if (!html.includes(key)) {
      console.warn('Missing translation key:', key);
      return;
    }

    html = html.replaceAll(key, translated);
  });

  /**
   * Restore original non-translatable HTML blocks.
   */
  nonTranslatable.forEach(({ key, html: original }) => {
    html = html.replaceAll(`<!--${key}-->`, original);
  });

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<body>${html}</body>`, 'text/html');

  /**
   * Restore protected href/src attribute values.
   */
  protectedAttrs.forEach(({ attr, key, value }) => {
    doc.querySelectorAll(`[${attr}]`).forEach((el) => {
      if (el.getAttribute(attr) === key) {
        el.setAttribute(attr, value);
      }
    });
  });

  return normalizeEntities(doc.body.innerHTML.replace(/\n{3,}/g, '\n\n'));
}
